import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatChipsModule } from '@angular/material/chips';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { KsefAdminApiService } from 'shared/services/api/ksef-admin.api.service';

@Component({
  selector: 'app-batch-session-details-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatTabsModule,
    MatTableModule,
    MatChipsModule,
    MatProgressSpinnerModule,
    MatTooltipModule
  ],
  templateUrl: './batch-session-details-dialog.component.html',
  styleUrls: ['./batch-session-details-dialog.component.scss']
})
export class BatchSessionDetailsDialogComponent implements OnInit {
  
  isLoading = true;
  isResendingAll = false;
  resendingInvoiceIds = new Set<string>();
  
  session: any = null;
  invoices: any[] = [];
  timeline: any[] = [];
  stats: any = {};

  invoicesColumns = ['documentNumber', 'documentType', 'status', 'ksefNumber', 'error', 'actions'];
  invoicesDataSource = new MatTableDataSource<any>([]);

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { sessionId: string },
    private dialogRef: MatDialogRef<BatchSessionDetailsDialogComponent>,
    private ksefAdminApi: KsefAdminApiService
  ) { }

  ngOnInit(): void {
    this.loadDetails();
  }

  loadDetails() {
    this.isLoading = true;

    this.ksefAdminApi.getBatchSessionDetails(this.data.sessionId).subscribe({
      next: (response) => {
        if (response.success) {
          this.session = response.data.session;
          this.invoices = response.data.invoices;
          this.timeline = response.data.timeline;
          this.stats = response.data.stats;

          this.invoicesDataSource.data = this.invoices;
        }
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Failed to load session details:', error);
        this.isLoading = false;
      }
    });
  }

  close() {
    this.dialogRef.close();
  }

  getInvoiceStatusIcon(invoice: any): string {
    if (invoice.ksefSent) return 'tabler_outline:check-circle';
    if (invoice.ksefError) return 'tabler_outline:alert-circle';
    if (invoice.ksefPendingSend) return 'tabler_outline:clock';
    return 'tabler_outline:help-circle';
  }

  getInvoiceStatusColor(invoice: any): string {
    if (invoice.ksefSent) return 'primary';
    if (invoice.ksefError) return 'warn';
    if (invoice.ksefPendingSend) return 'accent';
    return '';
  }

  getEventIcon(event: string): string {
    const icons: any = {
      'SESSION_CREATED': 'tabler_outline:plus-circle',
      'SESSION_CLOSED': 'tabler_outline:lock',
      'STATUS_CHECKED': 'tabler_outline:search',
      'SESSION_COMPLETED': 'tabler_outline:check-circle',
      'MANUAL_RETRY': 'tabler_outline:refresh'
    };
    return icons[event] || 'tabler_outline:point';
  }

  getEventLabel(event: string): string {
    const labels: any = {
      'SESSION_CREATED': 'Sesja utworzona',
      'SESSION_CLOSED': 'Sesja zamknięta',
      'STATUS_CHECKED': 'Sprawdzono status',
      'SESSION_COMPLETED': 'Sesja zakończona',
      'MANUAL_RETRY': 'Ręczny retry'
    };
    return labels[event] || event;
  }

  formatProcessingTime(ms: number | null): string {
    if (!ms) return '-';
    
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}min`;
  }

  /**
   * Czy sesja ma status błędu (timeout, error, 404)?
   */
  canResendSession(): boolean {
    return ['timeout', 'error'].includes(this.session?.status?.toLowerCase());
  }

  /**
   * Zwróć klasy CSS dla karty statusu
   */
  getStatusCardClass(status: string): string {
    const classes: any = {
      'pending': 'bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 text-yellow-800 dark:text-yellow-300',
      'checking': 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-300',
      'processing': 'bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 text-indigo-800 dark:text-indigo-300',
      'completed': 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-800 dark:text-green-300',
      'timeout': 'bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 text-orange-800 dark:text-orange-300',
      'error': 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-300'
    };
    return classes[status?.toLowerCase()] || 'bg-gray-50 dark:bg-gray-900/20 border border-gray-200 dark:border-gray-800 text-gray-800 dark:text-gray-300';
  }

  /**
   * Zwróć klasy CSS dla badge'a statusu faktury
   */
  getInvoiceStatusBadgeClass(invoice: any): string {
    if (invoice.ksefSent) {
      return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
    }
    if (invoice.ksefError) {
      return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
    }
    if (invoice.ksefPendingSend) {
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
    }
    return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300';
  }

  /**
   * Wyślij ponownie wszystkie faktury z sesji
   */
  resendAllInvoices() {
    if (!confirm(`Czy na pewno chcesz wysłać ponownie wszystkie faktury (${this.invoices.length}) z tej sesji?`)) {
      return;
    }

    this.isResendingAll = true;

    this.ksefAdminApi.resendAllInvoicesFromSession(this.data.sessionId, true).subscribe({
      next: (response) => {
        if (response.success) {
          alert(`✅ ${response.message}\n\nWysłano: ${response.data.sendResult?.success || 0}/${response.data.sendResult?.total || 0}`);
          this.dialogRef.close({ resent: true });
        }
        this.isResendingAll = false;
      },
      error: (error) => {
        console.error('Failed to resend invoices:', error);
        alert(`❌ Błąd podczas wysyłania faktur: ${error?.error?.message || 'Unknown error'}`);
        this.isResendingAll = false;
      }
    });
  }

  /**
   * Wyślij ponownie pojedynczą fakturę
   */
  resendSingleInvoice(invoice: any) {
    if (!confirm(`Czy na pewno chcesz wysłać ponownie fakturę ${invoice.documentNumber}?`)) {
      return;
    }

    const invoiceId = invoice.id || invoice._id; // Fallback dla starszych danych
    this.resendingInvoiceIds.add(invoiceId);

    this.ksefAdminApi.resendSingleInvoice(invoiceId, true).subscribe({
      next: (response) => {
        if (response.success) {
          alert(`✅ ${response.message}`);
          this.dialogRef.close({ resent: true });
        }
        this.resendingInvoiceIds.delete(invoiceId);
      },
      error: (error) => {
        console.error('Failed to resend invoice:', error);
        alert(`❌ Błąd podczas wysyłania faktury: ${error?.error?.message || 'Unknown error'}`);
        this.resendingInvoiceIds.delete(invoiceId);
      }
    });
  }

  isResendingInvoice(invoiceId: string): boolean {
    return this.resendingInvoiceIds.has(invoiceId);
  }
}

